import type { ApiResponse, GroundListQuery, SafeGround } from "@playarena/shared";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import { getEnv } from "@/utils/helpers";

export const groundApi = createApi({
  reducerPath: "groundApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${getEnv()?.VITE_API_URL}/${getEnv()?.VITE_APP_VERSION}/ground`,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Ground"],
  endpoints: builder => ({
    getGrounds: builder.query<ApiResponse<SafeGround[]>, GroundListQuery>({
      query: params => ({
        url: "/list",
        params,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update grounds list in slice
            dispatch({ type: "ground/setGrounds", payload: data.data });
            // Update pagination if available
            if (data.pagination) {
              dispatch({ type: "ground/setPagination", payload: data.pagination });
            }
          }
        } catch (error) {
          console.error("groundApi.getGrounds error:", error);
        }
      },
      providesTags: ["Ground"],
    }),

    getMyGrounds: builder.query<ApiResponse<SafeGround[]>, GroundListQuery>({
      query: params => ({
        url: "/list/my",
        params,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update grounds list in slice
            dispatch({ type: "ground/setGrounds", payload: data.data });
            // Update pagination if available
            if (data.pagination) {
              dispatch({ type: "ground/setPagination", payload: data.pagination });
            }
          }
        } catch (error) {
          console.error("groundApi.getMyGrounds error:", error);
        }
      },
      providesTags: ["Ground"],
    }),

    getGroundById: builder.query<ApiResponse<SafeGround>, string>({
      query: id => `/${id}`,
      onQueryStarted: async (_id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Set selected ground in slice
            dispatch({ type: "ground/setSelectedGround", payload: data.data });
          }
        } catch (error) {
          console.error("groundApi.getGroundById error:", error);
        }
      },
      providesTags: (result, error, id) => [{ type: "Ground", id }],
    }),

    createGround: builder.mutation<
      ApiResponse<SafeGround>,
      Omit<SafeGround, "id" | "createdAt" | "updatedAt">
    >({
      query: data => ({
        url: "/create",
        method: "POST",
        body: data,
      }),
      onQueryStarted: async (_data, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Add new ground to the grounds list
            dispatch({ type: "ground/addGround", payload: data.data });
          }
        } catch (error) {
          console.error("groundApi.createGround error:", error);
        }
      },
      invalidatesTags: ["Ground"],
    }),

    updateGround: builder.mutation<
      ApiResponse<SafeGround>,
      { id: string; data: Partial<SafeGround> }
    >({
      query: ({ id, data }) => ({
        url: `/update/${id}`,
        method: "PUT",
        body: data,
      }),
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update ground in grounds list
            dispatch({ type: "ground/updateGroundInList", payload: data.data });
            // Update selected ground if it's the one being updated
            dispatch({ type: "ground/setSelectedGround", payload: data.data });
          }
        } catch (error) {
          console.error("groundApi.updateGround error:", error);
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Ground", id }, "Ground"],
    }),

    deleteGround: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: id => ({
        url: `/delete/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success) {
            // Remove ground from grounds list
            dispatch({ type: "ground/removeGroundFromList", payload: id });
            // Clear selected ground if it's the one being deleted
            dispatch({ type: "ground/setSelectedGround", payload: null });
          }
        } catch (error) {
          console.error("groundApi.deleteGround error:", error);
        }
      },
      invalidatesTags: ["Ground"],
    }),

    getGroundsByManager: builder.query<ApiResponse<SafeGround[]>, string>({
      query: managerId => `/manager/${managerId}`,
      providesTags: ["Ground"],
    }),

    uploadGroundImages: builder.mutation<
      ApiResponse<{ images: string[] }>,
      { id: string; formData: FormData }
    >({
      query: ({ id, formData }) => ({
        url: `/${id}/images`,
        method: "POST",
        body: formData,
      }),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onQueryStarted: async (_params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // If we need to update the ground with new images, we would need to refetch
            // or update the ground data in the slice with the new image URLs
          }
        } catch (error) {
          console.error("groundApi.uploadGroundImages error:", error);
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: "Ground", id }],
    }),

    getGroundAvailability: builder.query<
      ApiResponse<{ available: boolean; events: any[] }>,
      { id: string; date: string }
    >({
      query: ({ id, date }) => `/${id}/availability?date=${date}`,
      providesTags: (result, error, { id }) => [{ type: "Ground", id }],
    }),

    searchGrounds: builder.query<ApiResponse<SafeGround[]>, GroundListQuery>({
      query: params => ({
        url: "/search",
        params,
      }),
      providesTags: ["Ground"],
    }),

    verifyGround: builder.mutation<ApiResponse<SafeGround>, string>({
      query: id => ({
        url: `/${id}/verify`,
        method: "PATCH",
      }),
      onQueryStarted: async (id, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.success && data.data) {
            // Update ground in grounds list
            dispatch({ type: "ground/updateGroundInList", payload: data.data });
            // Update selected ground if it's the one being verified
            dispatch({ type: "ground/setSelectedGround", payload: data.data });
          }
        } catch (error) {
          console.error("groundApi.verifyGround error:", error);
        }
      },
      invalidatesTags: (result, error, id) => [{ type: "Ground", id }, "Ground"],
    }),

    getGroundReviews: builder.query<
      ApiResponse<{ reviews: any[]; averageRating: number; totalReviews: number }>,
      string
    >({
      query: id => `/${id}/reviews`,
      providesTags: (result, error, id) => [{ type: "Ground", id }],
    }),
  }),
});

export const {
  useGetGroundsQuery,
  useGetMyGroundsQuery,
  useGetGroundByIdQuery,
  useCreateGroundMutation,
  useUpdateGroundMutation,
  useDeleteGroundMutation,
  useGetGroundsByManagerQuery,
  useUploadGroundImagesMutation,
  useGetGroundAvailabilityQuery,
  useSearchGroundsQuery,
  useVerifyGroundMutation,
  useGetGroundReviewsQuery,
} = groundApi;
